import { List, Map } from 'immutable';
import getIn from 'lodash/get';

import * as D from './deps';

export const INITIAL_STATE = Map({
  [D.PlaybackConstants.PlaylistKeys.CURRENT_PLAYLIST]: null,
  [D.PlaybackConstants.PlaylistKeys.PLAYLISTS]: Map({}),
});

// PLAYLIST REDUCER
export default D.createReducer(INITIAL_STATE, {
  [D.Constants.Playback.ActionTypes.SET_PLAYLIST]: setPlaylist,
  [D.Constants.Playback.ActionTypes.REMOVE_STATION]: onRemoveStation,
  [D.Constants.AudioPlayer.AudioPlayerActions.UPDATE]: audioUpdate,
  [D.Constants.AudioPlayer.AudioPlayerActions.AUDIO_PLAY_TRACK]: playTrack,
  [D.Constants.Playback.ActionTypes.INSERT_TRACK_INTO_PLAYLIST]: insertTrackIntoPlaylist,
  [D.Constants.Playback.ActionTypes.REMOVE_ARTIST_AUDIO_MESSAGES_FROM_PLAYLIST]:
    removeArtistAudioMessageTracksFromCurrentPlaylist,
  [D.Constants.UseCases.ActionTypes.ADVANCE_PLAYLIST]: advancePlaylist,
  [D.Constants.Api.ApiActions.LOGIN_SUCCESS]: clearPlaylist,
  [D.Constants.Api.ApiActions.ANON_LOGIN_SUCCESS]: clearPlaylist,
  [D.Constants.Api.ApiActions.DEVICE_LOGIN_SUCCESS]: clearPlaylist,
  [D.Constants.Api.ApiActions.ANON_LOGIN_ATTEMPT]: resetCurrentPlaylist,
  [D.Constants.Api.ApiActions.REGISTER_SUCCESS]: clearPlaylist,
  [D.Constants.Api.ApiActions.GET_FRAGMENT_SUCCESS]: updatePlaylistFromFragment,
  [D.Constants.Playback.ActionTypes.RESET_PLAYLIST_INDEX]: resetPlaylistIndex,
  [D.Constants.UseCases.ActionTypes.SET_CURRENT_TRACK_IN_PLAYLIST]: setCurrentTrackInPlaylist,
  [D.PlaybackConstants.ActionKeys.SET_ARE_VOICE_TRACKS_ENABLED]: setAreVoiceTracksEnabled,
  [D.PlaybackConstants.ActionKeys.LOAD_PREFETCHED_TRACKS]: loadPrefetchedTracks,
});

function getInitialStateWithOwnerId(ownerId) {
  return INITIAL_STATE.set(D.PlaybackConstants.PlaylistKeys.PLAYLIST_OWNER, ownerId);
}

function setPlaylist(state, action) {
  const { id, index, playlistType, trackModel } = action.data;
  const tracks = getIn(action, `data.${D.Constants.AudioPlayer.AudioPlayerKeys.PLAYLIST}`);

  if (tracks) {
    const areVoiceTracksEnabled = state.getIn(
      [
        D.PlaybackConstants.PlaylistKeys.PLAYLISTS,
        id,
        D.PlaybackConstants.PlaylistKeys.ARE_VOICE_TRACKS_ENABLED,
      ],
      true,
    );
    return state.withMutations(substate => {
      substate
        .set(D.PlaybackConstants.PlaylistKeys.CURRENT_PLAYLIST, id)
        .mergeIn([D.PlaybackConstants.PlaylistKeys.PLAYLISTS, id], {
          [D.PlaybackConstants.PlaylistKeys.CURRENT_INDEX]: index,
          [D.PlaybackConstants.PlaylistKeys.TRACKS]: tracks,
          [D.PlaybackConstants.PlaylistKeys.PLAYLIST_SOURCE_TYPE]: playlistType,
          [D.PlaybackConstants.PlaylistKeys.PLAYLIST_TRACK_MODEL_TYPE]:
            trackModel || 'PlaylistTrack',
          [D.PlaybackConstants.PlaylistKeys.ARE_VOICE_TRACKS_ENABLED]: areVoiceTracksEnabled,
        });
    });
  }

  return state.set(D.PlaybackConstants.PlaylistKeys.CURRENT_PLAYLIST, id);
}

function onRemoveStation(state, action) {
  const { data } = action;

  if (
    data &&
    data.stationId &&
    data.stationId === state.get(D.PlaybackConstants.PlaylistKeys.CURRENT_PLAYLIST)
  ) {
    return state.setIn([D.PlaybackConstants.PlaylistKeys.CURRENT_PLAYLIST], null);
  }

  return state;
}

function loadPrefetchedTracks(state, action) {
  const stationId = getIn(action, 'data.stationId', null);
  const prefetchedTracks = state.getIn([
    D.PlaybackConstants.PlaylistKeys.PLAYLISTS,
    stationId,
    D.PlaybackConstants.PlaylistKeys.PREFETCHED_TRACKS,
  ]);
  if (stationId && prefetchedTracks && prefetchedTracks.size > 0) {
    return state.mergeIn([D.PlaybackConstants.PlaylistKeys.PLAYLISTS, stationId], {
      [D.PlaybackConstants.PlaylistKeys.CURRENT_INDEX]: -1,
      [D.PlaybackConstants.PlaylistKeys.TRACKS]: prefetchedTracks,
      [D.PlaybackConstants.PlaylistKeys.PREFETCHED_TRACKS]: List(),
      [D.PlaybackConstants.PlaylistKeys.PLAYLIST_SOURCE_TYPE]:
        D.Constants.SourceTypes.SourceTypes.STATION,
      [D.PlaybackConstants.PlaylistKeys.PLAYLIST_TRACK_MODEL_TYPE]: 'PlaylistTrack',
    });
  }
  return state;
}

function updatePlaylistFromFragment(state, action) {
  const stationId = getIn(action, 'params.content.stationId', null);
  const tracks = getIn(action, 'response.result.tracks', []);
  const isPrefetch = getIn(action, 'params.actionParams.isPrefetch', false);
  if (tracks && stationId) {
    if (isPrefetch) {
      return state.mergeIn([D.PlaybackConstants.PlaylistKeys.PLAYLISTS, stationId], {
        [D.PlaybackConstants.PlaylistKeys.PREFETCHED_TRACKS]: List(tracks),
      });
    }
    return state.mergeIn([D.PlaybackConstants.PlaylistKeys.PLAYLISTS, stationId], {
      [D.PlaybackConstants.PlaylistKeys.CURRENT_INDEX]: -1,
      [D.PlaybackConstants.PlaylistKeys.TRACKS]: List(tracks),
      [D.PlaybackConstants.PlaylistKeys.PREFETCHED_TRACKS]: List(),
      [D.PlaybackConstants.PlaylistKeys.PLAYLIST_SOURCE_TYPE]:
        D.Constants.SourceTypes.SourceTypes.STATION,
      [D.PlaybackConstants.PlaylistKeys.PLAYLIST_TRACK_MODEL_TYPE]: 'PlaylistTrack',
    });
  }
  return state;
}

function setCurrentTrackInPlaylist(state, action) {
  const { id, track } = action.data;
  if (track && id) {
    const playlist = state.getIn([D.PlaybackConstants.PlaylistKeys.PLAYLISTS, id], null);
    if (!playlist) return state;
    const currentIndex = playlist.get(D.PlaybackConstants.PlaylistKeys.CURRENT_INDEX);
    const trackAtCurrentIndex = playlist.getIn(
      [D.PlaybackConstants.PlaylistKeys.TRACKS, currentIndex],
      null,
    );
    const isNewTrackAtCurrentIndex =
      trackAtCurrentIndex === D.Utils.Core.getFromMap(track, 'trackToken');
    const tracks = playlist.get(D.PlaybackConstants.PlaylistKeys.TRACKS);
    return state.mergeIn([D.PlaybackConstants.PlaylistKeys.PLAYLISTS, id], {
      [D.PlaybackConstants.PlaylistKeys.TRACKS]: isNewTrackAtCurrentIndex
        ? tracks
        : tracks.insert(currentIndex, D.Utils.Core.getFromMap(track, 'trackToken')),
      [D.PlaybackConstants.PlaylistKeys.CURRENT_TOKEN]: D.Utils.Core.getFromMap(
        track,
        'trackToken',
      ),
    });
  } else if (!track) {
    return state.setIn(
      [
        D.PlaybackConstants.PlaylistKeys.PLAYLISTS,
        id,
        D.PlaybackConstants.PlaylistKeys.CURRENT_TOKEN,
      ],
      null,
    );
  }
  return state;
}

function resetPlaylistIndex(state, action) {
  const index = action.data.index || 0;
  const playlist = action.data.id;
  const firstTrack = state
    .getIn(
      [
        D.PlaybackConstants.PlaylistKeys.PLAYLISTS,
        playlist,
        D.PlaybackConstants.PlaylistKeys.TRACKS,
      ],
      List(),
    )
    .get(index);
  return state.mergeIn([D.PlaybackConstants.PlaylistKeys.PLAYLISTS, playlist], {
    [D.PlaybackConstants.PlaylistKeys.CURRENT_INDEX]: index,
    [D.PlaybackConstants.PlaylistKeys.CURRENT_TRACK_TIME]: 0,
    [D.PlaybackConstants.PlaylistKeys.CURRENT_TRACK_DURATION]: 0,
    [D.PlaybackConstants.PlaylistKeys.CURRENT_TOKEN]: firstTrack,
  });
}

function audioUpdate(state, { payload }) {
  const currentPlaylist = state.get(D.PlaybackConstants.PlaylistKeys.CURRENT_PLAYLIST);
  return state.mergeIn([D.PlaybackConstants.PlaylistKeys.PLAYLISTS, currentPlaylist], {
    [D.PlaybackConstants.PlaylistKeys.CURRENT_TRACK_TIME]:
      payload[D.Constants.AudioPlayer.AudioPlayerKeys.CURRENT_TIME],
    [D.PlaybackConstants.PlaylistKeys.CURRENT_TRACK_DURATION]:
      payload[D.Constants.AudioPlayer.AudioPlayerKeys.DURATION],
    [D.PlaybackConstants.PlaylistKeys.LAST_PLAYED]: Date.now(),
  });
}

function resetCurrentPlaylist(state) {
  return state.set(D.PlaybackConstants.PlaylistKeys.CURRENT_PLAYLIST, null);
}

function advancePlaylist(state, action) {
  const { playlist } = action.data;
  const currentIndex = state.getIn([
    D.PlaybackConstants.PlaylistKeys.PLAYLISTS,
    playlist,
    D.PlaybackConstants.PlaylistKeys.CURRENT_INDEX,
  ]);
  const newIndex = currentIndex + 1;
  return state.setIn(
    [
      D.PlaybackConstants.PlaylistKeys.PLAYLISTS,
      playlist,
      D.PlaybackConstants.PlaylistKeys.CURRENT_INDEX,
    ],
    newIndex,
  );
}

function playTrack(state, { payload: { track, playlistId } }) {
  return state.setIn(
    [
      D.PlaybackConstants.PlaylistKeys.PLAYLISTS,
      playlistId,
      D.PlaybackConstants.PlaylistKeys.CURRENT_TOKEN,
    ],
    D.Utils.Core.getFromMap(track, 'trackToken'),
  );
}

function insertTrackIntoPlaylist(state, action) {
  const playlistId = action.data.playlist;
  const index = action.data.index;
  const trackToken = action.data.trackToken;
  const playlist = state.getIn(
    [
      D.PlaybackConstants.PlaylistKeys.PLAYLISTS,
      playlistId,
      D.PlaybackConstants.PlaylistKeys.TRACKS,
    ],
    List(),
  );
  const playlistIndex = playlist.size < index ? playlist.size : index;
  return state.mergeIn([D.PlaybackConstants.PlaylistKeys.PLAYLISTS, playlistId], {
    [D.PlaybackConstants.PlaylistKeys.TRACKS]: playlist.insert(playlistIndex, trackToken),
  });
}

function removeArtistAudioMessageTracksFromCurrentPlaylist(state, action) {
  const artistAudioMessageTokenSet = action && action.data && action.data.artistAudioMessageSet;
  if (!artistAudioMessageTokenSet || artistAudioMessageTokenSet.size === 0) {
    return state;
  }

  const currentPlaylistId = state.get(D.PlaybackConstants.PlaylistKeys.CURRENT_PLAYLIST);
  const playlistTrackTokens = state.getIn([
    D.PlaybackConstants.PlaylistKeys.PLAYLISTS,
    currentPlaylistId,
    D.PlaybackConstants.PlaylistKeys.TRACKS,
  ]);
  if (!playlistTrackTokens) {
    return state;
  }

  const newPlaylistTrackTokens = playlistTrackTokens.filter(playlistTrackToken => {
    return !artistAudioMessageTokenSet.has(playlistTrackToken);
  });

  if (newPlaylistTrackTokens === playlistTrackTokens) {
    return state;
  }

  return state.setIn(
    [
      D.PlaybackConstants.PlaylistKeys.PLAYLISTS,
      currentPlaylistId,
      D.PlaybackConstants.PlaylistKeys.TRACKS,
    ],
    newPlaylistTrackTokens,
  );
}

function clearPlaylist(state, action) {
  const isUserInitiated = action.isUserInitiated || action.isCustomerServiceLogin;
  const listenerId = getIn(action, 'sharedState.listenerId', null);

  if (isUserInitiated) {
    return getInitialStateWithOwnerId(listenerId);
  }
  return state;
}

function setAreVoiceTracksEnabled(state, action) {
  const { areVoiceTracksEnabled, playlistId } = action.data;
  return state.setIn(
    [
      D.PlaybackConstants.PlaylistKeys.PLAYLISTS,
      playlistId,
      D.PlaybackConstants.PlaylistKeys.ARE_VOICE_TRACKS_ENABLED,
    ],
    areVoiceTracksEnabled,
  );
}
